'use client';

import { useUserEventLogger } from '@repo/tracking-service';
import { IconButton } from '@repo/ui';
import { useEffect, useState } from 'react';

const BOTTOM_THRESHOLD_PX = 100;
const MIN_SCROLLABLE_HEIGHT_PX = 150;

const getScrollEl = () => (document.scrollingElement || document.documentElement) as HTMLElement;

const isAtBottom = () => {
  const el = getScrollEl();
  return el.scrollHeight - (el.scrollTop + el.clientHeight) <= BOTTOM_THRESHOLD_PX;
};

const hasEnoughScroll = () => {
  const el = getScrollEl();
  return el.scrollHeight - el.clientHeight >= MIN_SCROLLABLE_HEIGHT_PX;
};

interface ScrollButtonProps {
  direction: 'top' | 'bottom';
  footerVisible?: boolean;
}

export const ScrollButton = ({ direction, footerVisible }: ScrollButtonProps) => {
  const { sendClickEvent } = useUserEventLogger();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // direction이 'top'이면 footer와 동일하게 표시/숨김
    if (direction === 'top') {
      setIsVisible(footerVisible ?? false);
      return;
    }

    // direction이 'bottom'이면 스크롤 위치에 따라 표시/숨김
    const updateScrollButton = () => {
      // 충분한 스크롤 높이가 있고, 바닥이 아닐 때만 버튼 표시
      setIsVisible(hasEnoughScroll() && !isAtBottom());
    };

    updateScrollButton();

    window.addEventListener('scroll', updateScrollButton, { passive: true });
    window.addEventListener('resize', updateScrollButton, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollButton);
      window.removeEventListener('resize', updateScrollButton);
    };
  }, [direction, footerVisible]);

  const handleClick = () => {
    const el = getScrollEl();
    if (direction === 'top') {
      sendClickEvent('scroll-top_clicked');
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      sendClickEvent('scroll-bottom_clicked');
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <div
      className={`fixed left-0 bottom-[120px] z-50 flex w-[100%] justify-center ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
      style={{
        transition: 'all 300ms cubic-bezier(0.2, 0, 0, 1)'
      }}
    >
      <IconButton
        serviceName={'sena'}
        variant="main-foreground"
        size={'xsmall'}
        iconType={direction === 'top' ? 'arrow-up' : 'arrow-down'}
        onClick={handleClick}
        aria-label={direction === 'top' ? 'scroll to top' : 'scroll to bottom'}
        className="rounded-[var(--circular,1000px)] bg-white/80 shadow-[0_0_0_1px_rgba(0,0,0,0.08)] backdrop-blur-[10px]"
      />
    </div>
  );
};
